import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Heart,
  Coffee,
  Star,
  Rocket,
  Gift,
  Check,
  Home,
  Bookmark,
  MapPin,
  Users,
  User,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const SUPPORT_TIERS = [
  {
    id: 'coffee',
    name: 'Buy a Coffee',
    price: '$5',
    emoji: '☕',
    description: 'Support our caffeine addiction!',
    perks: ['Our eternal gratitude', 'Name in supporters list'],
    color: 'amber',
  },
  {
    id: 'supporter',
    name: 'Supporter',
    price: '$15',
    emoji: '⭐',
    description: 'Power up your travel adventures',
    perks: ['Everything in Coffee', 'Early access to features', 'Priority support'],
    popular: true,
    color: 'teal',
  },
  {
    id: 'champion',
    name: 'Champion',
    price: '$50',
    emoji: '🚀',
    description: 'Become a founding member',
    perks: ['Everything in Supporter', '3 months Pro free', 'Exclusive badge', 'Direct line to team'],
    color: 'rose',
  },
];

export default function Support() {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(null);
  
  const handleSupport = (tier) => {
    setSelectedTier(tier.id);
    // In real app, this would open payment gateway
    toast.success(`Thank you for supporting Panchi! ${tier.emoji}`);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">Support</span>
          </div>
          
          <div className="w-8" />
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-24 h-24 bg-rose-100 flex items-center justify-center mx-auto mb-6"
            style={{ borderRadius: '1.5rem' }}
          >
            <Heart size={48} className="text-rose-500" fill="currentColor" />
          </motion.div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
            Support <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">Panchi</span>
          </h1>
          <p className="text-gray-600 text-lg max-w-xl mx-auto">
            Panchi is built by a small team with big dreams. Your support helps us create better travel experiences for everyone.
          </p>
        </motion.div>
        
        {/* Tiers */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {SUPPORT_TIERS.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-6 bg-white/80 backdrop-blur border-2 shadow-lg hover:shadow-xl transition-all ${
                tier.popular ? 'border-teal-400' : 'border-gray-100'
              }`}
              style={{ borderRadius: '1.5rem' }}
            >
              {tier.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-bold" style={{ borderRadius: '9999px' }}>
                  Most Popular
                </span>
              )}
              
              <div className="text-center mb-6">
                <span className="text-5xl mb-4 block">{tier.emoji}</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <p className="text-gray-500 text-sm">{tier.description}</p>
              </div>
              
              <div className="text-center mb-6">
                <span className={`text-4xl font-black ${
                  tier.color === 'amber' ? 'text-amber-600' :
                  tier.color === 'teal' ? 'text-teal-600' : 'text-rose-600'
                }`}>
                  {tier.price}
                </span>
              </div>
              
              <ul className="space-y-3 mb-6">
                {tier.perks.map((perk, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check size={16} className="text-teal-500" />
                    {perk}
                  </li>
                ))}
              </ul>
              
              <button
                onClick={() => handleSupport(tier)}
                className={`w-full py-3 font-bold transition-all ${
                  tier.popular
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{ borderRadius: '9999px' }}
                data-testid={`support-${tier.id}`}
              >
                Support {tier.emoji}
              </button>
            </motion.div>
          ))}
        </div>
        
        {/* PayPal Link */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-center"
          style={{ borderRadius: '1.5rem' }}
        >
          <h3 className="text-lg font-bold text-gray-900 mb-2">Prefer PayPal?</h3>
          <p className="text-gray-600 text-sm mb-4">
            You can also support us directly via PayPal
          </p>
          <a
            href="https://paypal.me/panchi"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0070ba] text-white font-semibold shadow-lg hover:shadow-xl transition-all"
            style={{ borderRadius: '9999px' }}
            data-testid="paypal-link"
          >
            <ExternalLink size={18} />
            PayPal.me/panchi
          </a>
        </motion.div>
        
        {/* Thank You Message */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            Every contribution, big or small, helps us fly higher. 🐦
          </p>
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
